<template>
	<div class="scroll-pane">
		<div
			v-if="!contactId"
			class="h-100 d-flex align-items-center justify-content-center text-muted"
		>
			<div class="text-center">
				<i class="bi bi-cursor-fill" style="font-size: 48px"></i>
				<p class="mt-2">Select a contact from the list</p>
			</div>
		</div>
		<div
			v-else-if="loading"
			class="d-flex align-items-center justify-content-center text-muted py-5"
		>
			<span
				class="spinner-border spinner-border-sm me-2"
				role="status"
				aria-hidden="true"
			></span>
			Loading contact…
		</div>
		<div v-else-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>
		<template v-else-if="detail">
			<div class="card shadow-sm mb-3">
				<div class="card-header bg-white pane-header">
					Contact '{{ detail.name }}'
				</div>
				<div class="card-body">
					<h6 class="text-muted small mb-2">
						<i class="bi bi-envelope me-1"></i> Email Addresses
					</h6>
					<div v-if="emails.length > 0">
						<div v-for="email in emails" :key="email">{{ email }}</div>
					</div>
					<p v-else class="text-muted mb-0">No email addresses</p>

					<h6 class="text-muted small mb-2 mt-3">
						<i class="bi bi-telephone me-1"></i> Phone Numbers
					</h6>
					<div v-if="phones.length > 0">
						<div v-for="phone in phones" :key="phone.e164">
							<PhoneLinks :phone="phone" />
						</div>
					</div>
					<p v-else class="text-muted mb-0">No phone numbers</p>

					<h6 class="text-muted small mb-2 mt-3">
						<i class="bi bi-geo-alt me-1"></i> Addresses
					</h6>
					<div v-if="addresses.length > 0">
						<div
							v-for="address in addresses"
							:key="formatContactAddress(address)"
						>
							{{ formatContactAddress(address) }}
						</div>
					</div>
					<p v-else class="text-muted mb-0">No known addresses</p>

					<h6 class="text-muted small mb-2 mt-3">
						<i class="bi bi-flag me-1"></i> Reported addresses
					</h6>
					<div v-if="reportedAddresses.length > 0">
						<div v-for="a in reportedAddresses" :key="a.report_public_id">
							{{ formatContactReportedAddress(a) }}
							<span class="text-muted small ms-1">
								· {{ formatReportType(a.report_type) }} ·
								{{ a.report_public_id }}
								<template v-if="a.report_count > 1"
									>· {{ a.report_count }} reports</template
								>
							</span>
						</div>
					</div>
					<p v-else class="text-muted mb-0">No reported addresses</p>

					<h6 class="text-muted small mb-2 mt-3">
						<i class="bi bi-building me-1"></i> Sites
					</h6>
					<div v-if="sites.length > 0" class="d-flex flex-wrap gap-2">
						<span
							v-for="link in sites"
							:key="`${link.site_id}-${link.role}`"
							class="badge bg-light text-dark border fw-normal"
						>
							Site #{{ link.site_id }} · {{ link.role }}
							<template v-if="link.is_primary">· primary</template>
						</span>
					</div>
					<p v-else class="text-muted mb-0">No linked sites</p>
				</div>
			</div>

			<CardMessageHistory :messages="messages" />

			<CardEmailHistory :messages="emailHistory" :contact-emails="emails" />
		</template>
	</div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { apiClient } from "@/client";
import CardEmailHistory from "@/components/CardEmailHistory.vue";
import CardMessageHistory from "@/components/CardMessageHistory.vue";
import PhoneLinks from "@/components/PhoneLinks.vue";
import { formatContactAddress, formatContactReportedAddress } from "@/format";
import { Message, type ContactDetailDTO } from "@/type/api";

// The detail pane owns its own fetch: the list row it came from carries no
// messages, no linked Sites and no email log, and rendering from a row that a
// slower response later replaces is what made the pane flash stale data.
interface Props {
	contactId?: string;
}

const props = defineProps<Props>();

const detail = ref<ContactDetailDTO | null>(null);
const loading = ref(false);
const error = ref("");

// Monotonic request token. Every id change bumps it and clears the previous
// payload before awaiting, and any response whose token is no longer current is
// dropped, so a slow answer for contact A can never land under contact B.
let token = 0;

async function load(contactId: string): Promise<void> {
	token++;
	const current = token;
	detail.value = null;
	error.value = "";
	loading.value = contactId !== "";
	if (contactId === "") {
		return;
	}
	try {
		const payload = await apiClient.JSONGet<ContactDetailDTO>(
			`/api/contact/${contactId}`,
		);
		if (current !== token) {
			return;
		}
		detail.value = payload;
	} catch (e: unknown) {
		if (current !== token) {
			return;
		}
		error.value =
			e instanceof Error && e.message !== ""
				? e.message
				: "The contact could not be loaded.";
	} finally {
		if (current === token) {
			loading.value = false;
		}
	}
}

watch(
	() => props.contactId,
	(id) => void load(id ?? ""),
	{ immediate: true },
);

// The payload's collections are read through non-nil computeds: Go marshals a
// nil slice as null, and a contact with no linked Site must render an empty
// group rather than break the pane.
const emails = computed(() => detail.value?.emails ?? []);
const phones = computed(() => detail.value?.phones ?? []);
const addresses = computed(() => detail.value?.addresses ?? []);
const reportedAddresses = computed(
	() => detail.value?.reported_addresses ?? [],
);
const sites = computed(() => detail.value?.sites ?? []);
const emailHistory = computed(() => detail.value?.email_history ?? []);
const messages = computed(() => {
	return (detail.value?.messages ?? []).map((msg) => Message.fromJSON(msg));
});

// The reported-address tag words a report's type the way the rest of the review
// screens state an enum: its own spelling, first letter capitalized.
const formatReportType = (reportType: string): string => {
	return reportType.charAt(0).toUpperCase() + reportType.slice(1);
};
</script>
